import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import MatterService from '../../../../../AppSettings/services/MatterService';
import toastService from '../../../../../AppSettings/services/ToastService';
import AddTeamMemberModal from './AddTeamMemberModal';
import './TeamWidget.css';

interface TeamMember {
  id: string;
  employeeId: string;
  name: string;
  role: string;
  avatarUrl?: string;
  isLead?: boolean;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const toName = (first: any, last: any) =>
  `${first ?? ''} ${last ?? ''}`.trim();

const isValidName = (name: string) =>
  name.length > 0 && !/^null\s+null$/i.test(name);

const TeamWidget: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchTeam = useCallback(async () => {
    if (!matterId) return;
    setLoading(true);
    try {
      const details = await MatterService.getMatterDetails(matterId);
      const members: TeamMember[] = [];

      const leaderDetails: any = details?.employee_details;
      if (leaderDetails && details?.employeeid) {
        const leaderId = String(details.employeeid);
        const name = toName(leaderDetails.firstname, leaderDetails.lastname);
        if (isValidName(name)) {
          members.push({
            id: `leader-${leaderId}`,
            employeeId: leaderId,
            name,
            role: leaderDetails.position || leaderDetails.title || 'Leader',
            avatarUrl:
              leaderDetails.profile_picture ||
              leaderDetails.avatar ||
              leaderDetails.imageUrl ||
              leaderDetails.image ||
              undefined,
            isLead: true,
          });
        }
      }

      if (Array.isArray(details?.associated_employees)) {
        details.associated_employees.forEach((emp: any) => {
          const empId = String(emp.employeeid ?? emp.employeeId ?? '');
          if (!empId || empId === 'null') return;
          const name = toName(emp.firstname, emp.lastname);
          if (!isValidName(name)) return;
          members.push({
            id: `member-${empId}`,
            employeeId: empId,
            name,
            role: emp.position || emp.role || emp.title || 'Associate',
            avatarUrl:
              emp.profile_picture ||
              emp.avatar ||
              emp.imageUrl ||
              emp.image ||
              undefined,
            isLead: false,
          });
        });
      }

      setTeam(members);
    } catch (err) {
      console.error('Failed to load team', err);
      setTeam([]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const hasTeam = useMemo(() => team.length > 0, [team]);
  const currentEmployeeIds = useMemo(
    () => team.map((m) => m.employeeId),
    [team]
  );

  const handleRemoveMember = async (member: TeamMember) => {
    if (member.isLead || !matterId) return;
    if (!window.confirm(`Remove ${member.name} from the team?`)) return;
    try {
      await MatterService.updateAssociatedEmployees(matterId, 'remove', [
        member.employeeId,
      ]);
      toastService.success('Team member removed.');
      fetchTeam();
    } catch (err) {
      toastService.error('Failed to remove team member.');
    }
  };

  return (
    <div className="sidebar-card">
      <div className="sidebar-card__header">
        <div className="sidebar-card__title">
          <FontAwesomeIcon icon={faUsers} /> Team Members
        </div>
        <button
          type="button"
          className="team-widget-add-btn"
          onClick={() => setShowAddModal(true)}
          title="Add team member"
          disabled={loading}
        >
          <FontAwesomeIcon icon={faPlus} /> Add
        </button>
      </div>
      {!hasTeam && !loading && (
        <div className="sidebar-card__empty">No team members yet.</div>
      )}
      {loading && <div className="sidebar-card__empty">Loading...</div>}
      {hasTeam && (
        <div className="team-list">
          {team.map((member) => (
            <div className="team-row" key={member.id}>
              <div className="team-avatar">
                {member.avatarUrl ? (
                  <img src={member.avatarUrl} alt={member.name} />
                ) : (
                  <span>{getInitials(member.name)}</span>
                )}
              </div>
              <div className="team-meta">
                <div className="team-name">
                  {member.name}
                  {member.isLead && <span className="team-badge">Lead</span>}
                </div>
                <div className="team-role">{member.role}</div>
              </div>
              {!member.isLead && (
                <button
                  type="button"
                  className="team-widget-remove-btn"
                  onClick={() => handleRemoveMember(member)}
                  title={`Remove ${member.name}`}
                  aria-label={`Remove ${member.name}`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {showAddModal && matterId && (
        <AddTeamMemberModal
          matterId={matterId}
          currentEmployeeIds={currentEmployeeIds}
          onClose={() => setShowAddModal(false)}
          onAdded={fetchTeam}
        />
      )}
    </div>
  );
};

export default TeamWidget;
